import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { ClipboardService } from 'ngx-clipboard';
import { ReplaySubject } from 'rxjs';
import { filter, pairwise, startWith, takeUntil } from 'rxjs/operators';

import { coreSelectors } from 'app/core/state';
import { notNil } from 'app/utils/stream-util';
import Social from 'entity/Social';
import User from 'entity/User';

import { MessageRecord, WebsocketService, WebsocketStateType } from '../../services/websocket.service';
import { UserSubscriptionsState, socialAppSelectors } from '../../state/app';

@Component({
  selector: 'network-debugger',
  templateUrl: './network-debugger.component.html',
  styleUrls: ['./network-debugger.component.scss'],
  standalone: false,
})
export class NetworkDebuggerComponent implements OnDestroy {
  private destroyed$ = new ReplaySubject<boolean>(1);
  public subscriptions?: UserSubscriptionsState;

  public websocketState: WebsocketStateType = WebsocketStateType.CONNECTED;
  public messageHistory: MessageRecord[] = [];
  protected filter = '';
  protected messageDisplay: MessageRecord[] = [];
  public social?: Social;
  public user?: User;
  protected filterStats = true;

  public constructor(
    private websocketService: WebsocketService,
    private clipboardService: ClipboardService,
    private store: Store,
    private http: HttpClient,
  ) {
    this.websocketService.state$.pipe(takeUntil(this.destroyed$)).subscribe((state) => (this.websocketState = state));

    this.websocketService.messageHistory$.pipe(takeUntil(this.destroyed$)).subscribe((message) => {
      this.messageHistory.unshift(message);
      let messageeUpdate = this.messageHistory;
      if (this.filterStats) {
        messageeUpdate = this.messageHistory.filter(
          (m) => !['pub-stats', 'sub-stats', 'overall-stats'].some((stat) => JSON.stringify(m).includes(stat)),
        );
      }
      if (this.filter && this.filter.length > 0) {
        messageeUpdate = messageeUpdate.filter((m) => JSON.stringify(m).includes(this.filter));
      }
      this.messageDisplay = messageeUpdate;
    });

    this.store
      .select(coreSelectors.getUser)
      .pipe(takeUntil(this.destroyed$))
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .subscribe(({ isGuest, user }) => {
        this.user = user;
      });

    this.store
      .select(socialAppSelectors.getCurrentSocial)
      .pipe(
        startWith({} as Social),
        pairwise(),
        filter(([prevRoom, room]) => notNil(room) && notNil(prevRoom)),
        takeUntil(this.destroyed$),
      )
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .subscribe(([prevRoom, room]) => {
        this.social = room;
      });
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  protected get closeUrl() {
    const httpsUrl = this.websocketUrl?.replace('wss://', 'https://') ?? '';
    const url = new URL(httpsUrl);
    return `${url.protocol}//${url.hostname}/close/${this.social?.socialId}/${this.user?.userId}`;
  }

  protected async requestServerSocketClose() {
    await this.http.get(this.closeUrl).toPromise();
  }

  public onSimulateFailureClick() {
    this.websocketService.simulateFailure();
  }

  public onSimulateCloseClick() {
    this.websocketService.simulateClose();
  }

  public onSimulateIncompatibleVersionClick() {
    this.websocketService.simulateIncompatibleVersion();
  }

  public onCopyWebsocketMessages() {
    this.clipboardService.copyFromContent(JSON.stringify(this.messageHistory.slice().reverse(), null, ' '));
  }

  protected onClearWebsocketMessages() {
    this.messageHistory = [];
    this.messageDisplay = [];
  }
  protected onFilterChange() {
    this.messageDisplay = this.messageHistory.filter((message) => {
      return JSON.stringify(message).includes(this.filter);
    });
  }

  public get websocketUrl(): string | undefined {
    return this.websocketService.url;
  }

  public get videoServer(): string {
    return JSON.stringify(this.websocketService.videoServer, null, 2);
  }
}
